import { Component } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import Swiper, { Navigation, Thumbs, EffectFade } from 'swiper';
import 'swiper/css/bundle';

Swiper.use([Navigation, Thumbs, EffectFade]);

@Component
export class TabbedShowcase extends ThemeComponent {
    private tabs!: NodeListOf<HTMLButtonElement>;
    private panels!: NodeListOf<HTMLElement>;
    private navContainer!: HTMLElement;
    private boundHandleTabClick!: (event: Event) => void;

    mainSwiper: Swiper | undefined = undefined;
    thumbsSwiper: Swiper | undefined = undefined;
    thumbsSwiper2: Swiper | undefined = undefined;
    tabSwiper: Swiper | undefined = undefined;

    mainActiveIndex: number = 0;
    mainPreviousIndex: number = 0;

    init(): void {
        this.navContainer = this.querySelector('.tabbed-showcase__nav') as HTMLElement;
        this.tabs = this.querySelectorAll('.tabbed-showcase__tab');
        this.panels = this.querySelectorAll('.tabbed-showcase__panel');

        if (this.tabs.length === 0) {return;}

        this.boundHandleTabClick = this.handleTabClick.bind(this);
        this.bindEvents();

        this.initSwipers();
    }

    private bindEvents(): void {
        for (const tab of this.tabs) {
            tab.addEventListener('click', this.boundHandleTabClick);
        }
    }

    private handleTabClick(event: Event): void {
        const target = event.currentTarget as HTMLButtonElement;
        const tabIndex = target.getAttribute('data-tab');
        if (!tabIndex) {return;}

        this.setActiveTab(target);
        this.setActivePanel(tabIndex);
    }

    private setActiveTab(activeTab: HTMLButtonElement): void {
        for (const tab of this.tabs) {
            tab.classList.remove('active');
        }
        activeTab.classList.add('active');
    }

    private setActivePanel(index: string): void {
        for (const panel of this.panels) {
            panel.classList.remove('active');
        }
        const targetPanel = this.querySelector(`[data-panel="${index}"]`) as HTMLElement;
        targetPanel?.classList.add('active');

        // initialize swipers for the newly active panel
        this.destroySwipers();
        this.initSwipers();
    }

    private initSwipers(): void {
        const activePanel = this.querySelector('.tabbed-showcase__panel.active');
        if (!activePanel) {
            // eslint-disable-next-line no-console
            console.warn('No active panel found');
            return;
        }

        const tabSwiperEl = this.navContainer;
        const swiperEl = activePanel.querySelector('.mainGallery') as HTMLElement;
        const thumbsSwiperEl = activePanel.querySelector('.thumbsGallery-1') as HTMLElement;
        const thumbsSwiperEl2 = activePanel.querySelector('.thumbsGallery-2') as HTMLElement;

        try {
            // Initialize tab swiper
            if (tabSwiperEl) {
                this.tabSwiper = new Swiper(tabSwiperEl, {
                    slidesPerView: 'auto',
                    freeMode: true,
                    spaceBetween: 10
                });
            }

            // Initialize thumbnail swipers first
            if (thumbsSwiperEl) {
                this.thumbsSwiper = this.createThumbsSwiper(thumbsSwiperEl, 1);
            }

            if (thumbsSwiperEl2) {
                this.thumbsSwiper2 = this.createThumbsSwiper(thumbsSwiperEl2, 2);
            }

            // Initialize main swiper
            if (swiperEl) {
                this.mainSwiper = new Swiper(swiperEl, {
                    spaceBetween: 10,
                    allowTouchMove: false,
                    simulateTouch: true,
                    grabCursor: true,
                    effect: 'fade',
                    fadeEffect: {
                        crossFade: true,
                    },
                    on: {
                        slideChange: () => {
                            this.mainPreviousIndex = this.mainSwiper?.previousIndex ?? 0;
                            this.mainActiveIndex = this.mainSwiper?.activeIndex ?? 0;
                        }
                    }
                });
            }

            // Set up click handlers for thumbnail interactions
            this.setupThumbnailInteractions();

        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error initializing swipers:', error);
        }
    }

    private setupThumbnailInteractions(): void {
        if (this.thumbsSwiper) {
            this.thumbsSwiper.on('click', () => {
                const index = this.thumbsSwiper?.clickedIndex ?? 0;
                this.switchToThumbnailGallery(1, index);
                this.thumbsSwiper?.slideTo(this.mainPreviousIndex);
            });
        }

        if (this.thumbsSwiper2) {
            this.thumbsSwiper2.on('click', () => {
                const index = this.thumbsSwiper2?.clickedIndex ?? 0;
                this.switchToThumbnailGallery(2, index);
                this.thumbsSwiper2?.slideTo(this.mainPreviousIndex);
            });
        }
    }

    private switchToThumbnailGallery(galleryNumber: 1 | 2, slideIndex: number): void {
        if (!this.mainSwiper) {return;}

        // Slide the main gallery to the clicked thumbnail's index
        this.mainSwiper.slideTo(slideIndex);

        // If you want to highlight the active thumbnail manually:
        this.updateThumbnailActiveStates(galleryNumber, slideIndex);
    }

    private updateThumbnailActiveStates(activeGallery: 1 | 2, activeIndex: number): void {
        const activePanel = this.querySelector('.tabbed-showcase__panel.active');
        if (!activePanel) {return;}

        // Remove active class from all thumbnails
        const allThumbs =
            activePanel.querySelectorAll('.thumbsGallery-1 .swiper-slide, .thumbsGallery-2 .swiper-slide');

        for (const thumb of allThumbs) {
            thumb.classList.remove('swiper-slide-thumb-active');
        }

        // Add active class to the clicked thumbnail
        const targetGallery = activeGallery === 1 ? '.thumbsGallery-1' : '.thumbsGallery-2';
        const activeThumb = activePanel.querySelector(`${targetGallery} .swiper-slide:nth-child(${activeIndex + 1})`);
        activeThumb?.classList.add('swiper-slide-thumb-active');
    }

    private destroySwipers(): void {
        if (this.mainSwiper) {
            this.mainSwiper.destroy(true, true);
            this.mainSwiper = undefined;
        }
        if (this.thumbsSwiper) {
            this.thumbsSwiper.destroy(true, true);
            this.thumbsSwiper = undefined;
        }
        if (this.thumbsSwiper2) {
            this.thumbsSwiper2.destroy(true, true);
            this.thumbsSwiper2 = undefined;
        }
    }

    private createThumbsSwiper(el: HTMLElement, initialSlide = 0): Swiper {
        return new Swiper(el, {
            spaceBetween: 10,
            watchSlidesProgress: false,
            slideToClickedSlide: true,
            allowTouchMove: false,
            simulateTouch: true,
            grabCursor: false,
            initialSlide,
            effect: 'fade',
            fadeEffect: {
                crossFade: true,
            }
        });
    }

    destroy(): void {
        if (this.boundHandleTabClick) {
            for (const tab of this.tabs) {
                tab.removeEventListener('click', this.boundHandleTabClick);
            }
        }

        this.destroySwipers();
    }
}
